import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import * as THREE from "three";
import { useThree } from "@react-three/fiber";
import DraggableObject from "./DraggableObject";
import { observer } from "mobx-react-lite";
import { useContext } from "react";
import { FloorplannerStoreContext } from "../../store/floorplannerStore";
import { Line, Text } from "@react-three/drei";
import { AreaFillSymbolType, AreaSymbolType, isAreaFillMovementLocked, isAreaFillSymbolType } from "../../types/floorplannerTypes";
import { projectToWorld } from "./projectToWorld";
import { editorStore } from "../../store/editorStore";
import { renderStore } from "../../store/renderStore";
import { SimpleText } from "../Text/SimpleText";
import PatternFillMesh from "./PatternFillMesh";
import { consumeShouldSuppressClickAfterPanningDrag } from "../../utils/panningGesture";
import { SELECTED_HANDLE_INTERACTION_Z, SELECTED_HANDLE_RENDER_ORDER } from "../../constants/canvas";
import { isPointOnWallInteractionArea } from "../../utils/wallHitTesting";
import { pointerEventHasObjectInFront } from "../../utils/interactionHitTesting";
import { CANVAS_ACCENT_COLOR, canvasAccentColor } from "../../constants/brandColors";
import { resolveFillOpacity } from "../../utils/fillOpacity";
import { formatArea } from "../../utils/conversions";

interface AreaSymbolProps {
    symbol: AreaSymbolType | AreaFillSymbolType;
    onDrag: (index: number, newPosition: [number, number]) => void;
    onDragStart: (event: React.PointerEvent) => void;
    onDragEnd: (event: React.PointerEvent) => void;
}

const AREA_FILL_HANDLE_CLUSTER_DISTANCE = 0.12;

const buildAreaFillHandleGroups = (vertices: THREE.Vector2[]): { position: THREE.Vector2; indices: number[] }[] => {
    const handleVertices = vertices.length > 1 && vertices[0].distanceToSquared(vertices[vertices.length - 1]) < 1e-9
        ? vertices.slice(0, -1)
        : vertices;
    const groups: { position: THREE.Vector2; indices: number[] }[] = [];

    handleVertices.forEach((vertex, index) => {
        const existingGroup = groups.find((group) => group.position.distanceTo(vertex) <= AREA_FILL_HANDLE_CLUSTER_DISTANCE);
        if (!existingGroup) {
            groups.push({ position: vertex.clone(), indices: [index] });
            return;
        }

        existingGroup.indices.push(index);
        existingGroup.position.set(0, 0);
        existingGroup.indices.forEach((groupIndex) => existingGroup.position.add(handleVertices[groupIndex]));
        existingGroup.position.multiplyScalar(1 / existingGroup.indices.length);
    });

    return groups;
};

const AreaSymbol: React.FC<AreaSymbolProps> = observer(({ symbol, onDrag }) => {
    const { gl, camera } = useThree();
    const groupRef = useRef<THREE.Group>(null);
    const floorplannerStore = useContext(FloorplannerStoreContext);
    const [area, setArea] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const lineWeight = floorplannerStore.lineWeight;
    const noopRaycast = () => null;
    const isAreaFill = isAreaFillSymbolType(symbol);
    const isMovementLocked = isAreaFill && isAreaFillMovementLocked(symbol);
    const areaFillInteractionsDisabled = isAreaFill && (editorStore.panning || isMovementLocked);
    const selectionHandlesVisible = editorStore.shouldShowSelectionHandles();

    /**
     * Locked room floors are excluded from group-drag selection commits, but when
     * every wall of the bound room is translating they should visually follow
     * (same rule as RoomLabel). Skip if DraggableObject already applies the offset.
     */
    const roomBoundObjectIds = isAreaFillSymbolType(symbol)
        ? symbol.roomBinding?.objectIds
        : undefined;
    const roomFollowOffset = useMemo(() => {
        if (!isAreaFill || !renderStore.isGroupTranslating) {
            return { x: 0, y: 0 };
        }
        if (renderStore.isObjectTranslating(symbol.id)) {
            return { x: 0, y: 0 };
        }
        if (!roomBoundObjectIds?.length) {
            return { x: 0, y: 0 };
        }
        const allWallsTranslating = roomBoundObjectIds.every((wallId) =>
            renderStore.translatingWallIds.has(wallId)
        );
        return allWallsTranslating ? renderStore.groupTranslateOffset : { x: 0, y: 0 };
    }, [
        isAreaFill,
        symbol.id,
        roomBoundObjectIds,
        renderStore.isGroupTranslating,
        renderStore.groupTranslateOffset,
        renderStore.translatingWallIds,
        renderStore.translatingObjectIds,
    ]);
    
    // Check if measurements should be shown based on zoom level
    const shouldShowMeasurements = editorStore.shouldShowMeasurements();
    
    // Calculate zoom-derived values directly (same as MeasurementLine) to ensure consistency
    // Accessing these functions will automatically track MobX observables via observer()
    const measurementScale = editorStore.getMeasurementVisibilityScale();
    const zoomLevelDivisor = editorStore.measurementZoomLevelDivisor();
    
    // Calculate values directly on each render (same pattern as MeasurementLine)
    const textFontSize = (14 / zoomLevelDivisor) * measurementScale;
    const textBoxPadding = (0.13 / zoomLevelDivisor) * measurementScale;
    const textBoxHeight = (0.16 / zoomLevelDivisor) * measurementScale;
    const textBoxWidth = (0.6 / zoomLevelDivisor) * measurementScale;

    // Check if the shape is closed
    const isClosedShape = (
        symbol.vertices.length > 1 &&
        areVerticesEqual(
            symbol.vertices[0],
            symbol.vertices[symbol.vertices.length - 1]
        )
    );

    // Calculate the centroid (center) of the polygon
    const calculateCentroid = useCallback(() => {
        if (symbol.vertices.length < 3) return new THREE.Vector3(0, 0, 0);

        let areaSum = 0;
        let cx = 0;
        let cy = 0;

        for (let i = 0; i < symbol.vertices.length; i++) {
            const j = (i + 1) % symbol.vertices.length;
            const xi = symbol.vertices[i].x;
            const yi = symbol.vertices[i].y;
            const xj = symbol.vertices[j].x;
            const yj = symbol.vertices[j].y;

            const crossProduct = xi * yj - xj * yi;
            areaSum += crossProduct;
            cx += (xi + xj) * crossProduct;
            cy += (yi + yj) * crossProduct;
        }

        const area = areaSum / 2;
        cx = cx / (6 * area);
        cy = cy / (6 * area);

        return new THREE.Vector3(cx, cy, 0);
    }, [symbol.vertices]);

    // Calculate the center position
    const centerPos = calculateCentroid();

    // Helper to calculate the area of the polygon
    const calculateArea = useCallback(() => {
        if (symbol.vertices.length < 3) return 0;
        let sum = 0;
        for (let i = 0; i < symbol.vertices.length; i++) {
            const j = (i + 1) % symbol.vertices.length;
            sum +=
                symbol.vertices[i].x * symbol.vertices[j].y -
                symbol.vertices[j].x * symbol.vertices[i].y;
        }
        return Math.abs(sum / 2);
    }, [symbol.vertices]);

    // Utility function to compare vertices
    function areVerticesEqual(v1: { x: number; y: number }, v2: { x: number; y: number }) {
        return v1.x === v2.x && v1.y === v2.y;
    }

    // Convert plain vertices to THREE.Vector2 instances for ShapeGeometry
    const shapeVertices = symbol.vertices.map(
        (v) => new THREE.Vector2(v.x, v.y)
    );
    const areaFillHandleGroups = useMemo(
        () => isAreaFill ? buildAreaFillHandleGroups(symbol.vertices) : [],
        [isAreaFill, symbol.vertices]
    );

    const selectWallUnderAreaFillPointer = (event: any): boolean => {
        if (!isAreaFill) return false;

        const [worldX, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
        const pointer = new THREE.Vector2(worldX, worldY);
        const wall = Array.from(floorplannerStore.wallsMap.values()).find((candidateWall) => (
            isPointOnWallInteractionArea(pointer, candidateWall, floorplannerStore.wallWidth)
        ));
        if (!wall) {
            return false;
        }

        event.stopPropagation();
        if (!editorStore.isShiftPressed && !editorStore.selections.some((selection) => selection.id === wall.id)) {
            editorStore.clearSelections();
        }
        editorStore.addSelection(wall);
        floorplannerStore.selectWall(wall.id);
        return true;
    };

    const handleAreaFillVertexPointerDown = (indices: number[], handlePosition: THREE.Vector2, event: any) => {
        if (!isAreaFill || areaFillInteractionsDisabled || isMovementLocked) return;
        event.stopPropagation();
        floorplannerStore.beginUndoTransaction("update areaFill vertices");
        floorplannerStore.recordSymbolForUndo(symbol.id);
        const originalVertices = symbol.vertices.map((vertex) => vertex.clone());
        const wasClosed = originalVertices.length > 1 && originalVertices[0].distanceToSquared(originalVertices[originalVertices.length - 1]) < 1e-9;
        const originalHandlePosition = handlePosition.clone();

        const handlePointerMove = (moveEvent: PointerEvent) => {
            const [worldX, worldY] = projectToWorld(moveEvent.clientX, moveEvent.clientY, gl, camera);
            const nextHandlePosition = new THREE.Vector2(worldX, worldY).sub(symbol.position);
            const delta = nextHandlePosition.sub(originalHandlePosition);
            const vertices = originalVertices.map((vertex) => vertex.clone());
            const lastIndex = vertices.length - 1;

            indices.forEach((index) => {
                vertices[index] = originalVertices[index].clone().add(delta);
            });

            if (wasClosed && lastIndex > 0) {
                if (indices.includes(0)) {
                    vertices[lastIndex] = vertices[0].clone();
                } else if (indices.includes(lastIndex)) {
                    vertices[0] = vertices[lastIndex].clone();
                }
            }

            floorplannerStore.updateSymbolProperty(symbol.id, "vertices", vertices);
        };

        const handlePointerUp = () => {
            gl.domElement.removeEventListener("pointermove", handlePointerMove);
            gl.domElement.removeEventListener("pointerup", handlePointerUp);
            floorplannerStore.recordSymbolChange(symbol.id, "modify");
            floorplannerStore.pushToUndoStack();
            floorplannerStore.setBlockDirty(false);
            floorplannerStore.setDirty();
        };

        gl.domElement.addEventListener("pointermove", handlePointerMove);
        gl.domElement.addEventListener("pointerup", handlePointerUp);
        floorplannerStore.setBlockDirty(true);
    };

    const handleAreaFillClick = (event: any) => {
        // Unselected fills are click-through (no hit target). Selected fills use
        // DraggableObject for interaction; this path only applies while panning.
        if (selectWallUnderAreaFillPointer(event)) return;
        if (!areaFillInteractionsDisabled || !symbol.selected) return;

        if (pointerEventHasObjectInFront(event.intersections, event.eventObject)) {
            return;
        }

        event.stopPropagation();

        if (consumeShouldSuppressClickAfterPanningDrag()) {
            return;
        }

        if (!editorStore.isShiftPressed) {
            editorStore.clearSelections();
        }

        editorStore.addSelection(symbol);
        floorplannerStore.updateSymbolProperty(symbol.id, "selected", true);
    };

    /**
     * Select a measure-area polygon from a click on the fill or the sqm label.
     * Yields to any real object sitting on top so furniture/walls remain selectable.
     */
    const handleMeasureAreaClick = (event: any) => {
        if (
            editorStore.areaConstructionMode ||
            editorStore.areaFillConstructionMode ||
            editorStore.areaBleedFillMode ||
            editorStore.wallConstructionMode ||
            editorStore.lineConstructionMode ||
            editorStore.rulerConstructionMode ||
            editorStore.ruler3DConstructionMode
        ) {
            return;
        }

        if (pointerEventHasObjectInFront(event.intersections, event.eventObject)) {
            return;
        }

        event.stopPropagation();

        if (consumeShouldSuppressClickAfterPanningDrag()) {
            return;
        }

        if (!editorStore.isShiftPressed) {
            editorStore.clearSelections();
        }

        editorStore.addSelection(symbol);
        floorplannerStore.updateSymbolProperty(symbol.id, "selected", true);
    };

    const handleMeasureAreaPointerEnter = () => {
        if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") {
            document.body.classList.add("cursor-vector");
        }
        setIsHovered(true);
    };

    const handleMeasureAreaPointerLeave = () => {
        if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") {
            document.body.classList.remove("cursor-vector");
        }
        setIsHovered(false);
    };

    useEffect(() => {
        setArea(calculateArea());
    }, [symbol.vertices, calculateArea]);


    if (isAreaFill) {
        const outlineColor = canvasAccentColor(!!symbol.selected, !!isHovered, symbol.lineColor ?? floorplannerStore.lineColor);
        const fillOpacity = resolveFillOpacity(symbol);
        const floorAreaBaseZ = -0.007;
        // Keep the fill hit target behind symbols/openings while still above the grid.
        const floorAreaInteractionZ = -0.033;
        const floorAreaHandleZ = SELECTED_HANDLE_INTERACTION_Z - floorAreaBaseZ;
        // Unselected room floors are visual-only so marquee selection can start over them.
        // Double-click on the selection plane selects a fill under the pointer.
        const isInteractable = !!symbol.selected;

        const areaFillVisual = (
            <group
                ref={groupRef}
                onPointerDown={(event) => {
                    if (!isInteractable) return;
                    selectWallUnderAreaFillPointer(event);
                }}
                onClick={handleAreaFillClick}
                onPointerEnter={() => {
                    if (!isInteractable) return;
                    if (areaFillInteractionsDisabled && !isMovementLocked) return;
                    if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector");
                    setIsHovered(true);
                }}
                onPointerLeave={() => {
                    if (!isInteractable) return;
                    if (areaFillInteractionsDisabled && !isMovementLocked) return;
                    if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.remove("cursor-vector");
                    setIsHovered(false);
                }}
            >
                {shapeVertices.length >= 3 && (
                    <>
                        <mesh raycast={noopRaycast}>
                            <shapeGeometry args={[new THREE.Shape(shapeVertices)]} />
                            <PatternFillMesh
                                fillColor={symbol.fillColor ?? 0xffffff}
                                fillPattern={symbol.fillPattern}
                                useWorldSpace={false}
                                useLocalPositionCoords={true}
                                side={THREE.DoubleSide}
                                opacity={fillOpacity}
                                transparent={fillOpacity < 1}
                                depthWrite={false}
                            />
                        </mesh>
                        {symbol.selected && (
                            <mesh position={[0, 0, 0.001]} raycast={noopRaycast}>
                                <shapeGeometry args={[new THREE.Shape(shapeVertices)]} />
                                <meshBasicMaterial color={CANVAS_ACCENT_COLOR} transparent opacity={0.22} side={THREE.DoubleSide} depthWrite={false} />
                            </mesh>
                        )}
                        {isInteractable && (
                            <mesh position={[0, 0, floorAreaInteractionZ]}>
                                <shapeGeometry args={[new THREE.Shape(shapeVertices)]} />
                                <meshBasicMaterial transparent opacity={0} side={THREE.DoubleSide} depthWrite={false} />
                            </mesh>
                        )}
                    </>
                )}
                <Line
                    points={symbol.vertices.map((v) => [v.x, v.y, 0.002])}
                    color={outlineColor}
                    lineWidth={symbol.lineWeight ?? lineWeight}
                    {...(symbol.lineType === "dashed" && {
                        dashed: true,
                        dashSize: 0.2,
                        gapSize: 0.15,
                    })}
                    raycast={noopRaycast}
                />
                {selectionHandlesVisible && symbol.selected && !isMovementLocked && areaFillHandleGroups.map((group, index) => (
                    <group
                        key={`${symbol.id}-vertex-group-${index}`}
                        position={[group.position.x, group.position.y, floorAreaHandleZ]}
                        renderOrder={SELECTED_HANDLE_RENDER_ORDER}
                        onPointerDown={(event) => handleAreaFillVertexPointerDown(group.indices, group.position, event)}
                    >
                        <mesh renderOrder={SELECTED_HANDLE_RENDER_ORDER}>
                            <circleGeometry args={[0.08, 18]} />
                            <meshBasicMaterial transparent opacity={0} depthTest={false} depthWrite={false} />
                        </mesh>
                        <mesh renderOrder={SELECTED_HANDLE_RENDER_ORDER}>
                            <circleGeometry args={[0.055, 18]} />
                            <meshBasicMaterial color={CANVAS_ACCENT_COLOR} depthTest={false} depthWrite={false} />
                        </mesh>
                    </group>
                ))}
            </group>
        );

        if (!isInteractable) {
            return (
                <group position={[
                    symbol.position.x + roomFollowOffset.x,
                    symbol.position.y + roomFollowOffset.y,
                    floorAreaBaseZ,
                ]}>
                    {areaFillVisual}
                </group>
            );
        }

        return (
            <group position={[roomFollowOffset.x, roomFollowOffset.y, floorAreaBaseZ]}>
                <DraggableObject
                    position={[symbol.position.x, symbol.position.y]}
                    onDragStart={() => setIsDragging(true)}
                    onDragEnd={() => setIsDragging(false)}
                    onDrag={() => undefined}
                    selectable={true}
                    attachmentId={symbol.id}
                    attachmentType="noAttachments"
                    symbol={symbol}
                    disableDrag={areaFillInteractionsDisabled}
                >
                    {areaFillVisual}
                </DraggableObject>
            </group>
        );
    }

    return (
        <group
            ref={groupRef}
            position={[symbol.position.x, symbol.position.y, 0.02]}
        >
            {symbol.vertices.length > 1 ? (
                <>
                    {/* Filled mesh for the shape (visual only — hit testing uses the interaction mesh) */}
                    {(editorStore.areaConstructionMode || symbol.selected) && (
                        <mesh raycast={noopRaycast}>
                            <shapeGeometry args={[new THREE.Shape(shapeVertices)]} />
                            <meshBasicMaterial
                                color={CANVAS_ACCENT_COLOR}
                                transparent={true}
                                opacity={0.3}
                                side={THREE.DoubleSide}
                            />
                        </mesh>
                    )}

                    {/* Invisible hit target so the whole marked area is selectable, not just the sqm label */}
                    {isClosedShape && shapeVertices.length >= 3 && !editorStore.areaConstructionMode && (
                        <mesh
                            onClick={handleMeasureAreaClick}
                            onPointerEnter={handleMeasureAreaPointerEnter}
                            onPointerLeave={handleMeasureAreaPointerLeave}
                        >
                            <shapeGeometry args={[new THREE.Shape(shapeVertices)]} />
                            <meshBasicMaterial
                                transparent
                                opacity={0}
                                side={THREE.DoubleSide}
                                depthWrite={false}
                            />
                        </mesh>
                    )}

                    {/* Outline */}
                    <Line
                        points={symbol.vertices.map((v) => [v.x, v.y, 0])}
                        color={(editorStore.areaConstructionMode || symbol.selected || isHovered) ? CANVAS_ACCENT_COLOR : "black"}
                        lineWidth={0.5}
                        dashed={true}
                        dashSize={0.2}
                        gapSize={0.15}
                        raycast={noopRaycast}
                    />

                    {/* Area label */}
                    {(area > 0) && editorStore.showMeasures && shouldShowMeasurements && (
                        <group
                            position={[centerPos.x, centerPos.y, 0.02]} // Slightly above the shape
                            onClick={handleMeasureAreaClick}
                            onPointerEnter={handleMeasureAreaPointerEnter}
                            onPointerLeave={handleMeasureAreaPointerLeave}
                        >
                            {/* Background Plane */}
                            <mesh position={[0, 0, -0.01]}>
                                <planeGeometry
                                    args={[
                                        textBoxWidth + textBoxPadding,
                                        textBoxHeight,
                                    ]}
                                />
                                <meshBasicMaterial color={"white"} />
                            </mesh>

                            {/* Text */}
                            <SimpleText
                                fontSize={textFontSize}
                                color="#222222"
                            >
                                {formatArea(area, editorStore.displayLengthUnit)}
                            </SimpleText>
                        </group>
                    )}
                </>
            ) : (
                // Render default symbol when there are not enough vertices
                <DraggableObject
                    position={[symbol.position.x, symbol.position.y]}
                    onDrag={(event) => {
                        const [worldX, worldY] = projectToWorld(
                            event[0],
                            event[1],
                            gl,
                            camera
                        );
                        onDrag(0, [worldX, worldY]);
                    }}
                    onDragStart={() => setIsDragging(true)}
                    onDragEnd={() => setIsDragging(false)}
                    selectable={true}
                >
                    <mesh>
                        <boxGeometry args={[1, 1, 1]} />
                        <meshStandardMaterial color="gray" />
                    </mesh>
                </DraggableObject>
            )}
        </group>
    );
});

export default AreaSymbol;
